import { Mail, Search, ShieldCheck, UserX, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import type { EligibleAuditor } from "../../services/auditorService";
import type { AdminLabRow } from "../../services/labService";

interface AdminLabAuditorModalProps {
  lab: AdminLabRow;
  allowedEmailDomains: string[];
  eligibleAuditors: EligibleAuditor[];
  isOpen: boolean;
  isLoadingEligible: boolean;
  eligibleError: string | null;
  isAssigning: boolean;
  isRemoving: boolean;
  isInviting: boolean;
  isClearingPendingInvite: boolean;
  onAssign: (auditorId: string) => void;
  onRemove: () => void;
  onInvite: (email: string) => void;
  onClearPendingInvite: () => void;
  onClose: () => void;
}

const formatLabel = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatAuditorName = (auditor: EligibleAuditor) => {
  return `${auditor.first_name} ${auditor.last_name}`.trim() || auditor.email;
};

const splitEmail = (value: string) => {
  const parts = value.split("@");
  if (parts.length !== 2) {
    return null;
  }

  const localPart = parts[0] ?? "";
  const domain = parts[1] ?? "";
  return {
    localPart: localPart.trim(),
    domain: domain.trim().toLowerCase(),
  };
};

export const AdminLabAuditorModal = ({
  lab,
  allowedEmailDomains = [],
  eligibleAuditors,
  isOpen,
  isLoadingEligible,
  eligibleError,
  isAssigning,
  isRemoving,
  isInviting,
  isClearingPendingInvite,
  onAssign,
  onRemove,
  onInvite,
  onClearPendingInvite,
  onClose,
}: AdminLabAuditorModalProps) => {
  const certification = lab.current_certification;
  const currentAuditor = certification?.auditor ?? null;
  const currentAuditorLabel = currentAuditor?.name || currentAuditor?.email || "Current auditor";
  const pendingInvite = certification?.pending_auditor_invite ?? null;
  const normalizedAllowedDomains = useMemo(
    () => allowedEmailDomains.map((domain) => domain.trim().toLowerCase()).filter(Boolean),
    [allowedEmailDomains],
  );
  const pendingInviteDomain = useMemo(() => {
    if (!pendingInvite?.email) {
      return null;
    }

    return splitEmail(pendingInvite.email)?.domain ?? null;
  }, [pendingInvite?.email]);
  const defaultInviteDomain = useMemo(() => {
    if (pendingInviteDomain && normalizedAllowedDomains.includes(pendingInviteDomain)) {
      return pendingInviteDomain;
    }

    return normalizedAllowedDomains[0] ?? "";
  }, [normalizedAllowedDomains, pendingInviteDomain]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAuditorId, setSelectedAuditorId] = useState<string | null>(
    currentAuditor?.id ?? null,
  );
  const [activeTab, setActiveTab] = useState<"existing" | "invite">("existing");
  const [inviteDomain, setInviteDomain] = useState(defaultInviteDomain);
  const [inviteLocalPart, setInviteLocalPart] = useState(
    splitEmail(pendingInvite?.email ?? "")?.localPart ?? "",
  );
  const [inviteOverrideEmail, setInviteOverrideEmail] = useState<string | null>(null);
  const [inviteWarning, setInviteWarning] = useState<string | null>(null);

  useEffect(() => {
    setSearchTerm("");
    setSelectedAuditorId(currentAuditor?.id ?? null);
    setActiveTab("existing");
    setInviteDomain(defaultInviteDomain);
    setInviteLocalPart(splitEmail(pendingInvite?.email ?? "")?.localPart ?? "");
    setInviteOverrideEmail(null);
    setInviteWarning(null);
  }, [currentAuditor?.id, lab.id, certification?.id, pendingInvite?.email, defaultInviteDomain]);

  const applyInviteInput = (rawValue: string) => {
    const normalizedValue = rawValue.trim().toLowerCase();

    if (!normalizedValue) {
      setInviteLocalPart("");
      setInviteOverrideEmail(null);
      setInviteWarning(null);
      return;
    }

    if (!normalizedValue.includes("@")) {
      setInviteLocalPart(normalizedValue.replace(/\s+/g, ""));
      setInviteOverrideEmail(null);
      setInviteWarning(null);
      return;
    }

    const parsedEmail = splitEmail(normalizedValue);
    if (!parsedEmail || !parsedEmail.localPart || !parsedEmail.domain) {
      setInviteLocalPart("");
      setInviteOverrideEmail(normalizedValue.replace(/\s+/g, ""));
      setInviteWarning("Using the full email as entered. The backend will validate it.");
      return;
    }

    if (!normalizedAllowedDomains.includes(parsedEmail.domain)) {
      setInviteLocalPart("");
      setInviteOverrideEmail(normalizedValue.replace(/\s+/g, ""));
      setInviteWarning(
        normalizedAllowedDomains.length > 1
          ? `Using ${normalizedValue} instead of the suggested org domains ${normalizedAllowedDomains.map((domain) => `@${domain}`).join(", ")}. The backend will validate it.`
          : `Using ${normalizedValue} instead of the suggested domain @${defaultInviteDomain}. The backend will validate it.`,
      );
      return;
    }

    setInviteDomain(parsedEmail.domain);
    setInviteLocalPart(parsedEmail.localPart);
    setInviteOverrideEmail(null);
    setInviteWarning(null);
  };

  const inviteInputValue = inviteOverrideEmail ?? inviteLocalPart;
  const fullInviteEmail = inviteOverrideEmail
    ? inviteOverrideEmail
    : inviteLocalPart.trim() && inviteDomain
      ? `${inviteLocalPart.trim().toLowerCase()}@${inviteDomain}`
      : "";

  const filteredAuditors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return eligibleAuditors;
    }

    return eligibleAuditors.filter((auditor) => {
      const haystack = `${formatAuditorName(auditor)} ${auditor.email}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [eligibleAuditors, searchTerm]);

  const selectedAuditor =
    selectedAuditorId === null
      ? null
      : eligibleAuditors.find((auditor) => auditor.user_id === selectedAuditorId) ?? null;
  const canAssign =
    selectedAuditor !== null && selectedAuditor.user_id !== currentAuditor?.id;
  const canInvite =
    Boolean(fullInviteEmail) &&
    (Boolean(inviteOverrideEmail) || Boolean(inviteDomain)) &&
    !isInviting &&
    !isAssigning &&
    !isRemoving &&
    !isClearingPendingInvite;
  const inviteActionLabel = isInviting
    ? "Sending..."
    : currentAuditor
      ? "Unassign & send invite"
      : pendingInvite
        ? "Update invite"
        : "Send invite";

  if (!isOpen || !certification) {
    return null;
  }

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl rounded-[28px] border border-[#e7dccb] bg-[#fffaf2] p-0 shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#eadfce] px-6 py-5 sm:px-8">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-base-content/55">
              Auditor Assignment
            </p>
            <div>
              <h3 className="text-2xl font-semibold text-base-content">{lab.name}</h3>
              <p className="mt-1 text-sm text-base-content/70">
                Assign an auditor to the active {certification.level} certification.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-1 text-sm">
              <span className="badge badge-outline border-[#d8c8b0] bg-white/80 text-base-content/80">
                {lab.type === "wet" ? "Wet Lab" : "Dry Lab"}
              </span>
              <span className="badge badge-outline border-[#d8c8b0] bg-white/80 text-base-content/80">
                {formatLabel(certification.workflow_status)}
              </span>
              <span className="badge badge-outline border-[#d8c8b0] bg-white/80 text-base-content/80">
                {formatLabel(certification.level)}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-circle btn-sm"
            onClick={onClose}
            disabled={isAssigning || isRemoving}
            aria-label="Close auditor assignment modal"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-5 sm:px-8">
          <div className="grid gap-4 rounded-3xl border border-[#eadfce] bg-white/85 p-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-base-content/55">
                Auditor
              </p>
              <p className="mt-1 text-base font-semibold text-base-content">
                {currentAuditor?.name || currentAuditor?.email || "No auditor assigned"}
              </p>
              {currentAuditor?.email && currentAuditor.name !== currentAuditor.email && (
                <p className="text-sm text-base-content/65">{currentAuditor.email}</p>
              )}
              {!currentAuditor && certification.auditor_status_detail && (
                <p className="mt-2 text-sm text-base-content/65">
                  {certification.auditor_status_detail}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-base-content/55">
                Champion
              </p>
              <p className="mt-1 text-base font-semibold text-base-content">
                {lab.champion?.name || lab.champion?.email || "No champion assigned"}
              </p>
              {lab.champion?.email && lab.champion.name !== lab.champion.email && (
                <p className="text-sm text-base-content/65">{lab.champion.email}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 px-1">
            <button
              type="button"
              className={`btn btn-sm ${activeTab === "existing" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setActiveTab("existing")}
            >
              Existing Members
            </button>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === "invite" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setActiveTab("invite")}
            >
              Invite New Member
            </button>
          </div>

          {activeTab === "existing" ? (
            <div className="rounded-3xl border border-[#eadfce] bg-white/85 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-lg font-semibold text-base-content">Potential Auditors</h4>
                  <p className="text-sm text-base-content/70">
                    Existing organisation members who are not in this lab can be assigned directly.
                  </p>
                </div>
                <label className="input input-bordered flex h-11 w-full items-center gap-2 border-[#d8c8b0] bg-[#fffaf2] sm:max-w-xs">
                  <Search className="h-4 w-4 text-base-content/45" aria-hidden="true" />
                  <input
                    type="text"
                    className="grow"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </label>
              </div>

              {eligibleError && (
                <div className="alert alert-error mt-4">
                  <span>{eligibleError}</span>
                </div>
              )}

              {isLoadingEligible ? (
                <div className="mt-4 space-y-3">
                  <div className="skeleton h-20 w-full rounded-2xl" />
                  <div className="skeleton h-20 w-full rounded-2xl" />
                  <div className="skeleton h-20 w-full rounded-2xl" />
                </div>
              ) : filteredAuditors.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-[#d8c8b0] bg-[#fffaf2] px-4 py-10 text-center">
                  <ShieldCheck className="mx-auto h-8 w-8 text-base-content/35" aria-hidden="true" />
                  <p className="mt-3 font-medium text-base-content">
                    {searchTerm ? "No matching auditors" : "No eligible auditors available"}
                  </p>
                  <p className="mt-1 text-sm text-base-content/65">
                    Use the invite tab to invite a new organisation member when no one eligible is available yet.
                  </p>
                </div>
              ) : (
                <div className="mt-4 max-h-[22rem] space-y-3 overflow-auto pr-1">
                  {filteredAuditors.map((auditor) => {
                    const isSelected = selectedAuditorId === auditor.user_id;
                    const isCurrentAuditor = currentAuditor?.id === auditor.user_id;

                    return (
                      <button
                        key={auditor.user_id}
                        type="button"
                        className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                          isSelected
                            ? "border-primary bg-primary/6 shadow-sm"
                            : "border-[#eadfce] bg-white hover:border-[#d8c8b0]"
                        }`}
                        onClick={() => setSelectedAuditorId(auditor.user_id)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-base-content">
                                {formatAuditorName(auditor)}
                              </p>
                              {isCurrentAuditor && (
                                <span className="badge badge-outline border-primary/35 bg-primary/10 text-primary">
                                  Current
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-base-content/70">{auditor.email}</p>
                          </div>
                          <span className="badge badge-outline border-[#d8c8b0] bg-[#fffaf2] text-base-content/75">
                            {auditor.current_audit_count} active
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-3xl border border-[#eadfce] bg-white/85 p-4">
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-base-content">Invite New Organisation Member</h4>
                  <p className="text-sm text-base-content/70">
                    This sends a standard organisation-membership invite and keeps the certification with the invited member until they sign up.
                  </p>
                </div>

                {currentAuditor && (
                  <div className="rounded-2xl border border-warning/35 bg-warning/10 p-4">
                    <p className="font-semibold text-base-content">This invite reassigns the certification.</p>
                    <p className="mt-1 text-sm text-base-content/75">
                      Sending it immediately unassigns {currentAuditorLabel}. The certification will stay under review with this invite pending until the new member signs up.
                    </p>
                  </div>
                )}

                {pendingInvite && (
                  <div className="rounded-2xl border border-[#d8c8b0] bg-[#fffaf2] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-base-content">Pending invite</p>
                        <p className="text-sm text-base-content/70">{pendingInvite.email}</p>
                      </div>
                      <span className="badge badge-outline border-[#d8c8b0] bg-white text-base-content/80">
                        {pendingInvite.state}
                      </span>
                    </div>
                    {certification.auditor_status_detail && (
                      <p className="mt-3 text-sm text-base-content/65">
                        {certification.auditor_status_detail}
                      </p>
                    )}
                  </div>
                )}

                <label className="form-control">
                  <span className="mb-2 text-sm font-semibold text-base-content">Invite email</span>
                  <div
                    className={`flex min-h-12 items-stretch overflow-hidden rounded-2xl border bg-[#fffaf2] transition ${
                      inviteWarning ? "border-warning" : "border-[#d8c8b0]"
                    }`}
                  >
                    <div className="flex items-center gap-2 px-4 text-base-content/55">
                      <Mail className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <input
                      type="text"
                      className="min-w-0 flex-1 bg-transparent px-1 text-base text-base-content outline-none"
                      placeholder="new-auditor"
                      value={inviteInputValue}
                      onChange={(event) => applyInviteInput(event.target.value)}
                      onPaste={(event) => {
                        const pastedValue = event.clipboardData.getData("text");
                        if (!pastedValue) {
                          return;
                        }

                        event.preventDefault();
                        applyInviteInput(pastedValue);
                      }}
                      disabled={!inviteDomain && !inviteOverrideEmail}
                      aria-invalid="false"
                      aria-describedby="invite-email-help invite-email-warning"
                    />
                    <div
                      className={`flex items-center border-l border-[#e7dccb] px-4 text-sm font-medium ${
                        inviteWarning
                          ? "bg-warning/10 text-warning line-through"
                          : "bg-[#f5ecdf] text-base-content/80"
                      }`}
                    >
                      {inviteDomain ? `@${inviteDomain}` : "No domain configured"}
                    </div>
                  </div>
                </label>

                <div className="space-y-1">
                  <p id="invite-email-help" className="text-sm text-base-content/65">
                    Type the part before the @. Pasting a full email will strip the domain when it matches an allowed organisation domain.
                  </p>
                  {normalizedAllowedDomains.length > 1 && (
                    <p className="text-sm text-base-content/55">
                      Allowed domains: {normalizedAllowedDomains.map((domain) => `@${domain}`).join(", ")}
                    </p>
                  )}
                  {inviteWarning && (
                    <p id="invite-email-warning" className="text-sm font-medium text-warning">
                      {inviteWarning}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-[#eadfce] px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div>
            {activeTab === "invite" && pendingInvite ? (
              <button
                type="button"
                className="btn btn-ghost text-error"
                onClick={onClearPendingInvite}
                disabled={isInviting || isAssigning || isRemoving || isClearingPendingInvite}
              >
                <UserX className="h-4 w-4" aria-hidden="true" />
                {isClearingPendingInvite ? "Clearing..." : "Clear pending invite"}
              </button>
            ) : currentAuditor ? (
              <button
                type="button"
                className="btn btn-ghost text-error"
                onClick={onRemove}
                disabled={isAssigning || isRemoving || isInviting || isClearingPendingInvite}
              >
                <UserX className="h-4 w-4" aria-hidden="true" />
                Remove current auditor
              </button>
            ) : null}
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={isAssigning || isRemoving || isInviting || isClearingPendingInvite}
            >
              Close
            </button>
            {activeTab === "existing" ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (selectedAuditorId) {
                    onAssign(selectedAuditorId);
                  }
                }}
                disabled={!canAssign || isAssigning || isRemoving || isInviting || isClearingPendingInvite}
              >
                {isAssigning ? "Saving..." : currentAuditor ? "Change auditor" : "Assign auditor"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onInvite(fullInviteEmail)}
                disabled={!canInvite}
              >
                {inviteActionLabel}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
};